import {
	AuthService,
	FilterService,
	ListNavigationService,
	getFieldValue as getNestedFieldValue,
} from '@supply-mover-2-0/core';
import { canViewClientDetail } from '@supply-mover-2-0/shared';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import { useNavigation } from '../../hooks/useNavigation';
import { isModifiedClick } from '../../utils/isModifiedClick';
import { useListBuilderContextSafe } from '../components/core/ListBuilderProvider';
import type {
	EnhancedFieldConfiguration,
	ListBuilderCallbacks,
} from '../types';

interface UseListLinkNavigationOptions {
	field: EnhancedFieldConfiguration;
	data: unknown;
	listKey?: string;
	objects?: Array<{ objectTable: string; baseUrl?: string }>;
	callbacks?: ListBuilderCallbacks;
}

interface UseListLinkNavigationResult {
	/** The resolved URL for the link, or null if unresolvable (e.g., callback type) */
	linkUrl: string | null;
	/** Click handler that saves list context on normal clicks and lets the browser handle modified clicks */
	handleLinkClick: (e: React.MouseEvent) => void;
	/** Whether this is a callback-type link (no URL — should render as button, not anchor) */
	isCallback: boolean;
}

/**
 * Pull the related client's salesperson info out of a row, trying the most
 * common shapes used across our list configs.
 *
 * - When the row IS a client: salesRepId / alternateSalesRepId / fkUserId / _fk_user__id
 * - When the row is something else and a client is nested: client.* / fkClient.*
 *
 * Returns null when nothing matches — the caller should treat that as
 * "data not available, fall through" rather than "access denied".
 */
function extractClientSalespersonInfo(
	row: unknown,
): { salesRepId: number | null; alternateSalesRepId: number | null } | null {
	if (row == null || typeof row !== 'object') {
		return null;
	}

	const candidates: Array<Record<string, unknown> | undefined> = [
		row as Record<string, unknown>,
		(row as Record<string, unknown>)['client'] as
			| Record<string, unknown>
			| undefined,
		(row as Record<string, unknown>)['fkClient'] as
			| Record<string, unknown>
			| undefined,
	];

	const primaryKeys = ['salesRepId', 'fkUserId', '_fk_user__id', '_fk_user_id'];
	const alternateKeys = [
		'alternateSalesRepId',
		'fkUserIdAlternate',
		'_fk_user__id__alternate',
		'_fk_user__id_alternate',
	];

	const coerceId = (value: unknown): number | null => {
		if (value == null) return null;
		if (typeof value === 'number' && !Number.isNaN(value)) return value;
		if (typeof value === 'string') {
			const parsed = Number.parseInt(value, 10);
			return Number.isNaN(parsed) ? null : parsed;
		}
		return null;
	};

	let foundPrimaryKey = false;
	let salesRepId: number | null = null;
	let alternateSalesRepId: number | null = null;

	for (const candidate of candidates) {
		if (candidate == null) continue;

		for (const key of primaryKeys) {
			if (key in candidate) {
				foundPrimaryKey = true;
				salesRepId = coerceId(candidate[key]);
				break;
			}
		}

		for (const key of alternateKeys) {
			if (key in candidate) {
				alternateSalesRepId = coerceId(candidate[key]);
				break;
			}
		}

		if (foundPrimaryKey) break;
	}

	// If we never saw a primary salesperson key, the row doesn't carry the
	// info we need — let the caller decide to fall through.
	return foundPrimaryKey ? { salesRepId, alternateSalesRepId } : null;
}

/** Validates that a value is a usable ID (non-NaN number or non-empty string) */
function isValidId(value: unknown): value is string | number {
	if (typeof value === 'number') {
		return !Number.isNaN(value);
	}

	if (typeof value === 'string') {
		return value.length > 0;
	}

	return false;
}

/** Extracts a record ID from data, trying a specific field or common ID field names */
function getRecordId(
	record: unknown,
	idField?: string,
): string | number | null {
	if (record == null) {
		return null;
	}

	const rec = record as Record<string, unknown>;

	if (idField != null) {
		const value = rec[idField];

		return isValidId(value) ? value : null;
	}

	// Try common ID field names in order
	const idFields = ['id', '_id', 'Id', 'ID', 'clientId', 'client_id'];

	for (const field of idFields) {
		const value = rec[field];

		if (isValidId(value)) {
			return value;
		}
	}

	return null;
}

/**
 * Resolves a URL template by replacing {placeholder} tokens with values from data.
 * Returns null if any placeholder cannot be resolved.
 */
function resolveUrlTemplate(
	template: string,
	data: Record<string, unknown>,
): string | null {
	let url = template;
	const matches = url.match(/\{([^}]+)\}/g);
	let hasUnresolvedPlaceholders = false;

	if (matches != null) {
		for (const match of matches) {
			const fieldPath = match.slice(1, -1);
			const value = getNestedFieldValue(data, fieldPath);

			if (value != null && String(value) !== '') {
				url = url.replace(match, String(value));
			} else {
				hasUnresolvedPlaceholders = true;
			}
		}
	}

	return hasUnresolvedPlaceholders ? null : url;
}

/**
 * Hook for handling link clicks in list builder display components.
 * Computes the target URL upfront so it can be used as an href on anchor elements,
 * enabling right-click "Open in New Tab" and middle-click behavior.
 * Automatically saves list context before SPA navigation to enable prev/next on detail pages.
 */
export function useListLinkNavigation(
	options: UseListLinkNavigationOptions,
): UseListLinkNavigationResult {
	const { field, data, listKey, objects, callbacks } = options;
	const navigation = useNavigation();
	const listContext = useListBuilderContextSafe();
	const { link } = field;

	const isCallback = link?.type === 'callback';

	// Compute the target URL upfront so it can be used as href
	const linkUrl = useMemo((): string | null => {
		if (link?.enabled !== true || data == null) {
			return null;
		}

		switch (link.type) {
			case 'object': {
				const targetObjectTable = link.objectTable ?? objects?.[0]?.objectTable;

				if (targetObjectTable != null || (objects?.length ?? 0) > 0) {
					const id = getRecordId(data, link.idField);

					if (id != null) {
						const objectConfig =
							objects?.find((obj) => obj.objectTable === targetObjectTable) ??
							objects?.[0];

						const resolvedUrl =
							objectConfig?.baseUrl != null
								? objectConfig.baseUrl.replace(':id', String(id))
								: `/${targetObjectTable ?? listKey}/${id}`;

						// Suppress client-detail links the caller can't open. We strict-check
						// when the row carries salesperson info (e.g. on the client list);
						// otherwise we fall through and rely on the route/API guard. Lists
						// where the row is pre-filtered by *:view:own (orders, invoices, etc.)
						// don't carry client salesperson info but only return rows the user
						// already owns, so the link is safe.
						if (targetObjectTable === 'client') {
							const authState = AuthService.getInstance().getAuthState();
							const currentUserId =
								authState.user?.instanceUserId ?? authState.user?.id ?? null;
							const clientInfo = extractClientSalespersonInfo(data);
							if (
								clientInfo != null &&
								!canViewClientDetail(clientInfo, currentUserId)
							) {
								return null;
							}
						}

						return resolvedUrl;
					}
				}

				return null;
			}
			case 'self': {
				const selfId = getRecordId(data, link.idField);

				if (selfId != null) {
					const selfObjectConfig = objects?.find(
						(obj) => obj.objectTable === listKey,
					);

					// Same gate as the 'object' branch: when the self-link points at a
					// client (the row IS a client), suppress the link if the caller can't
					// view this client's detail page.
					if (listKey === 'client' || listKey === 'clients') {
						const authState = AuthService.getInstance().getAuthState();
						const currentUserId =
							authState.user?.instanceUserId ?? authState.user?.id ?? null;
						const clientInfo = extractClientSalespersonInfo(data);
						if (
							clientInfo != null &&
							!canViewClientDetail(clientInfo, currentUserId)
						) {
							return null;
						}
					}

					return selfObjectConfig?.baseUrl != null
						? selfObjectConfig.baseUrl.replace(':id', String(selfId))
						: listKey != null
							? `/${listKey}/${selfId}`
							: null;
				}

				return null;
			}
			case 'custom': {
				const linkConfig = link as {
					customUrl?: string;
					urlTemplate?: string;
				};

				if (linkConfig.customUrl != null) {
					return linkConfig.customUrl;
				}

				if (linkConfig.urlTemplate != null) {
					return resolveUrlTemplate(
						linkConfig.urlTemplate,
						data as Record<string, unknown>,
					);
				}

				return null;
			}
			case 'callback':
			default:
				return null;
		}
	}, [link, data, listKey, objects]);

	const handleLinkClick = useCallback(
		(e: React.MouseEvent) => {
			// Always stop propagation to prevent table row click handlers from firing
			e.stopPropagation();

			if (link?.enabled !== true || data == null) {
				e.preventDefault();
				return;
			}

			// Handle callback type — always preventDefault since there's no URL
			if (link.type === 'callback') {
				e.preventDefault();

				if (link.callbackName != null && callbacks != null) {
					const callback = callbacks[link.callbackName];

					if (callback != null) {
						callback(data);
					}
				}

				return;
			}

			// For modified clicks (Cmd+click, Ctrl+click, middle-click, Shift+click),
			// let the browser handle navigation natively using the href attribute
			if (isModifiedClick(e)) {
				return;
			}

			// Normal left-click: prevent default, save list context, then navigate via SPA
			e.preventDefault();

			if (linkUrl == null) {
				return;
			}

			// Determine the list key for context saving
			let targetListKey = listKey ?? '';

			if (link.type === 'object') {
				const targetObjectTable = link.objectTable ?? objects?.[0]?.objectTable;
				const objectConfig =
					objects?.find((obj) => obj.objectTable === targetObjectTable) ??
					objects?.[0];

				// Check if navigating to the same list type based on URL pattern
				const isNavigatingToSameList =
					listKey != null &&
					objectConfig?.baseUrl != null &&
					objectConfig.baseUrl.startsWith(`/${listKey}`);

				targetListKey = isNavigatingToSameList
					? listKey
					: (targetObjectTable ?? listKey ?? '');
			}

			// Always include the current page as the returnPath so the destination's
			// back button can navigate back here regardless of which list type spawned
			// the navigation (main list, embedded tab, related-entity link, etc.).
			const returnPath = window.location.pathname + window.location.search;

			// Save list context if navigating within the same list type
			if (targetListKey === listKey && listContext != null && listKey != null) {
				const transformedFilters = FilterService.transformFiltersForBackend(
					listContext.state.filterState,
					{ listKey },
				);

				console.debug(
					'[ListNav] Saving context - filterState:',
					JSON.stringify(listContext.state.filterState),
				);
				console.debug(
					'[ListNav] Saving context - transformed filters:',
					JSON.stringify(transformedFilters),
				);

				const contextToSave = {
					listId: listContext.state.activeListId,
					listName: listContext.state.currentList?.name,
					sortFields: listContext.state.currentSortFields,
					filters: transformedFilters,
					filterSummary: ListNavigationService.buildFilterSummary(
						listContext.state.filterState,
					),
					sourceUrl: window.location.pathname + window.location.search,
					timestamp: Date.now(),
				};

				console.debug(
					'[ListNav] Full context being saved:',
					JSON.stringify(contextToSave),
				);
				ListNavigationService.saveListContext(listKey, contextToSave);

				navigation.navigateTo(linkUrl, {
					state: { fromList: true, returnPath },
				});
			} else {
				navigation.navigateTo(linkUrl, {
					state: { returnPath },
				});
			}
		},
		[link, data, listKey, objects, callbacks, navigation, listContext, linkUrl],
	);

	return { linkUrl, handleLinkClick, isCallback };
}
